import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { verifyEmail } from '../api/auth';
import { useAuth } from '../auth/AuthContext';
import { authErrorMessage } from '../auth/errors';
import { forgetLinkToken, takeLinkToken } from '../auth/linkToken';

const STORAGE_KEY = 'vbbplay.verify_token';

/** Confirmation needs a click, so mail scanners that prefetch links cannot consume the token. */
export function VerifyEmailPage() {
  const { t } = useTranslation(['auth', 'common', 'errors']);
  const { user, refresh } = useAuth();
  const [token] = useState(() => takeLinkToken(STORAGE_KEY));
  const [phase, setPhase] = useState<'ready' | 'done' | 'error'>(token ? 'ready' : 'error');
  const [error, setError] = useState<string | null>(token ? null : t('auth:linkInvalid'));
  const [submitting, setSubmitting] = useState(false);

  async function confirm() {
    if (!token) return;
    setSubmitting(true);
    try {
      await verifyEmail(token);
      forgetLinkToken(STORAGE_KEY);
      setPhase('done');
      if (user) await refresh();
    } catch (err) {
      setError(authErrorMessage(t, err, 'invalid_or_expired_token'));
      setPhase('error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page auth-page auth-sheet-page">
      <article className="auth-card auth-sheet">
        <h1>{t('auth:verifyTitle')}</h1>
        {phase === 'ready' ? (
          <>
            <p>{t('auth:verifyIntro')}</p>
            <button
              type="button"
              className="btn btn-primary btn-block"
              disabled={submitting}
              onClick={() => void confirm()}
            >
              {submitting ? t('common:loading') : t('auth:verifyCta')}
            </button>
          </>
        ) : null}
        {phase === 'done' ? (
          <p className="auth-success" role="status">
            {t('auth:verifyDone')}
          </p>
        ) : null}
        {phase === 'error' ? (
          <>
            <p className="auth-error" role="alert">
              {error}
            </p>
            {user && !user.email_verified ? <p className="muted-note">{t('auth:verifyResendHint')}</p> : null}
          </>
        ) : null}
        <p>
          <Link to={user ? '/security' : '/auth/login'}>{user ? t('auth:securityTitle') : t('auth:backToLogin')}</Link>
        </p>
      </article>
    </div>
  );
}
